import { useTranslation } from 'react-i18next';

import { DONUT_RADIUS, donutDash } from './chartGeometry';
import { scoreLevel } from '../../../resultCopy';

/**
 * 점수 도넛 게이지 (시안 §8.5 `음정 정확도 87/100 좋음`) — 242px, 굵은 링, 가운데에
 * 레드 점수 + `/100` + 세 칸 라벨(`좋음`).
 *
 * **값이 없으면 링을 안 그린다.** 빈 링에 숫자만 빼면 0점으로 읽히고, 회색 링만 그리면
 * "0점을 받았다"와 "아직 못 쟀다"가 같은 그림이 된다 — `ResultRow`가 값 없는 줄에
 * 미터를 안 넘기는 것과 같은 규칙이다.
 */

export const DonutGauge = ({
  score,
  label,
}: {
  /** 0..100. `null`이면 못 잰 것이다(0이 아니다). */
  score: number | null;
  /** 접근성 이름(화면에는 안 보인다 — 머리 카드가 이미 이름을 말한다). */
  label: string;
}) => {
  const { t } = useTranslation();

  if (score == null) {
    return (
      <div className="flex flex-col items-center gap-1 py-4">
        <span className="text-sm text-bk-600">{label}</span>
        <span className="text-sm font-semibold text-bk-600">
          {t('web.result.pending')}
        </span>
      </div>
    );
  }

  const [filled, gap] = donutDash(score);

  return (
    <div className="relative mx-auto h-[242px] w-[242px]">
      <svg
        viewBox="0 0 100 100"
        className="h-full w-full -rotate-90"
        role="img"
        aria-label={`${label} ${t('web.result.score', { value: Math.round(score) })}`}
      >
        {/* 바탕 링 — 채워질 자리가 어디까지인지 보여준다. */}
        <circle
          cx="50"
          cy="50"
          r={DONUT_RADIUS}
          fill="none"
          stroke="var(--bk-100)"
          strokeWidth={9}
        />
        <circle
          data-testid="donut-arc"
          cx="50"
          cy="50"
          r={DONUT_RADIUS}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={9}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${gap}`}
          // 0점일 때 `strokeLinecap="round"`가 점 하나를 남긴다 — 길이가 0이면 아예 숨긴다.
          opacity={filled === 0 ? 0 : 1}
        />
      </svg>
      {/* 숫자는 HTML로 얹는다 — SVG 텍스트는 뷰박스와 함께 늘어나 글꼴 크기가 흔들린다. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
        <p className="leading-7">
          <span className="text-2xl font-semibold text-primary tabular-nums">
            {Math.round(score)}
          </span>
          <span className="text-xl text-bk-600">/100</span>
        </p>
        <span className="text-base font-semibold text-bk-900">
          {t(`web.result.tabs.level.${scoreLevel(score)}`)}
        </span>
      </div>
    </div>
  );
};
